// src/pages/itsm/Assets.tsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sun, Moon } from 'lucide-react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Pill, Btn, StatTile, EmptyState, ConfirmDialog, showToast, ToastHost } from '../../components/ui/UI'

const STATUS_CONFIG: Record<string, { label: string; tone: 'success' | 'neutral' | 'warning' | 'danger' }> = {
    active: { label: 'Active', tone: 'success' },
    inactive: { label: 'Inactive', tone: 'neutral' },
    maintenance: { label: 'Maintenance', tone: 'warning' },
    disposed: { label: 'Disposed', tone: 'danger' },
    lost: { label: 'Lost', tone: 'danger' },
}

const CONDITION_CONFIG: Record<string, { label: string; tone: 'success' | 'info' | 'warning' | 'danger' }> = {
    new: { label: 'New', tone: 'success' },
    good: { label: 'Good', tone: 'info' },
    fair: { label: 'Fair', tone: 'warning' },
    poor: { label: 'Poor', tone: 'danger' },
}

export default function Assets() {
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [assets, setAssets] = useState<any[]>([])
    const [categories, setCategories] = useState<any[]>([])
    const [agents, setAgents] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [typeFilter, setTypeFilter] = useState('all')
    const [showAdd, setShowAdd] = useState(false)
    const [editingAsset, setEditingAsset] = useState<any>(null)
    const [showDetail, setShowDetail] = useState<any>(null)
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

    const [form, setForm] = useState({
        name: '', category_id: '', type: 'hardware', status: 'active',
        condition: 'good', serial_number: '', model: '', manufacturer: '',
        purchase_date: '', purchase_cost: '', warranty_expiry: '',
        location: '', assigned_to: '', assigned_user: '',
        ip_address: '', mac_address: '', os: '', notes: '',
    })

    useEffect(() => { fetchAll() }, [statusFilter, typeFilter])

    const fetchAll = async () => {
        let query = supabase
            .from('itsm_assets')
            .select('*, itsm_asset_categories(name, icon, color), itsm_agents!assigned_to(name)')
            .order('created_at', { ascending: false })

        if (statusFilter !== 'all') query = query.eq('status', statusFilter)
        if (typeFilter !== 'all') query = query.eq('type', typeFilter)

        const [{ data: a }, { data: cats }, { data: ag }] = await Promise.all([
            query,
            supabase.from('itsm_asset_categories').select('*').order('name'),
            supabase.from('itsm_agents').select('id, name').eq('is_active', true),
        ])

        setAssets(a || [])
        setCategories(cats || [])
        setAgents(ag || [])
        setLoading(false)
    }

    const saveAsset = async () => {
        const payload = {
            ...form,
            category_id: form.category_id || null,
            assigned_to: form.assigned_to || null,
            purchase_cost: form.purchase_cost ? Number(form.purchase_cost) : null,
            purchase_date: form.purchase_date || null,
            warranty_expiry: form.warranty_expiry || null,
        }
        if (editingAsset) {
            await supabase.from('itsm_assets').update(payload).eq('id', editingAsset.id)
            showToast('Asset updated', 'success')
        } else {
            await supabase.from('itsm_assets').insert([payload])
            showToast('Asset added', 'success')
        }
        resetForm()
        fetchAll()
    }

    const deleteAsset = async (id: number) => {
        await supabase.from('itsm_assets').delete().eq('id', id)
        showToast('Asset deleted', 'success')
        fetchAll()
    }

    const resetForm = () => {
        setForm({ name: '', category_id: '', type: 'hardware', status: 'active', condition: 'good', serial_number: '', model: '', manufacturer: '', purchase_date: '', purchase_cost: '', warranty_expiry: '', location: '', assigned_to: '', assigned_user: '', ip_address: '', mac_address: '', os: '', notes: '' })
        setShowAdd(false)
        setEditingAsset(null)
    }

    const filtered = assets.filter(a =>
        a.name?.toLowerCase().includes(search.toLowerCase()) ||
        a.asset_tag?.toLowerCase().includes(search.toLowerCase()) ||
        a.serial_number?.toLowerCase().includes(search.toLowerCase()) ||
        a.assigned_user?.toLowerCase().includes(search.toLowerCase())
    )

    const stats = {
        total: assets.length,
        active: assets.filter(a => a.status === 'active').length,
        maintenance: assets.filter(a => a.status === 'maintenance').length,
        totalValue: assets.reduce((s, a) => s + (a.purchase_cost || 0), 0),
    }

    const s = styles

    if (loading) return (
        <div className="jr-app" data-theme={theme} style={s.loading}>Loading...</div>
    )

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            <button onClick={() => navigate('/dashboard/itsm')} style={s.back}>← Back to ITSM</button>

            <div style={s.header}>
                <div>
                    <h1 style={s.title}>Asset Management</h1>
                    <p style={s.subtitle}>{assets.length} total assets</p>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                        onClick={toggleTheme}
                        aria-label="Toggle dark mode"
                        style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: `1px solid ${T.borderStrong}`, background: T.surface, color: T.text2, cursor: 'pointer' }}
                    >
                        {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                    </button>
                    <Btn variant="primary" style={undefined} onClick={() => setShowAdd(true)}>+ Add Asset</Btn>
                </div>
            </div>

            {/* Stats */}
            <Card style={{ padding: 0, display: 'flex', overflow: 'hidden', marginBottom: 20 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <StatTile label="Total Assets" value={stats.total} tone="info" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <StatTile label="Active" value={stats.active} tone="success" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <StatTile label="In Maintenance" value={stats.maintenance} tone="warning" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <StatTile label="Total Value" value={`SAR ${stats.totalValue.toLocaleString()}`} tone="violet" />
                </div>
            </Card>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <input style={s.search} placeholder="Search assets..." value={search} onChange={e => setSearch(e.target.value)} />
                <select style={s.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="all">All Status</option>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select style={s.select} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                    <option value="all">All Types</option>
                    <option value="hardware">Hardware</option>
                    <option value="software">Software</option>
                    <option value="network">Network</option>
                    <option value="other">Other</option>
                </select>
            </div>

            {/* Table */}
            <Card style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                            {['Tag', 'Name', 'Category', 'Type', 'Status', 'Condition', 'Location', 'Assigned To', 'Warranty', ''].map(h => (
                                <th key={h} style={s.th}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(asset => {
                            const status = STATUS_CONFIG[asset.status] || STATUS_CONFIG.active
                            const condition = CONDITION_CONFIG[asset.condition] || CONDITION_CONFIG.good
                            const warrantyExpired = asset.warranty_expiry && new Date(asset.warranty_expiry) < new Date()

                            return (
                                <tr key={asset.id} style={s.tr}
                                    onClick={() => setShowDetail(asset)}>
                                    <td style={s.td}>
                                        <span className="jr-mono" style={{ fontSize: 11, color: T.text3, fontWeight: 700 }}>{asset.asset_tag}</span>
                                    </td>
                                    <td style={s.td}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontSize: 16 }}>{asset.itsm_asset_categories?.icon || '💻'}</span>
                                            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{asset.name}</span>
                                        </div>
                                    </td>
                                    <td style={s.td}><span style={{ fontSize: 12, color: T.text2 }}>{asset.itsm_asset_categories?.name || '—'}</span></td>
                                    <td style={s.td}><Pill tone="neutral">{asset.type}</Pill></td>
                                    <td style={s.td}><Pill tone={status.tone}>{status.label}</Pill></td>
                                    <td style={s.td}><span style={{ fontSize: 11, fontWeight: 700, color: T[condition.tone] }}>{condition.label}</span></td>
                                    <td style={s.td}><span style={{ fontSize: 12, color: T.text2 }}>{asset.location || '—'}</span></td>
                                    <td style={s.td}><span style={{ fontSize: 12, color: T.text2 }}>{asset.itsm_agents?.name || asset.assigned_user || '—'}</span></td>
                                    <td style={s.td}>
                                        {asset.warranty_expiry ? (
                                            <span style={{ fontSize: 11, fontWeight: 600, color: warrantyExpired ? T.danger : T.success }}>
                                                {warrantyExpired ? '⚠️ Expired' : `✅ ${new Date(asset.warranty_expiry).toLocaleDateString('en-GB')}`}
                                            </span>
                                        ) : '—'}
                                    </td>
                                    <td style={s.td} onClick={e => e.stopPropagation()}>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <Btn size="sm" style={undefined} onClick={() => {
                                                setEditingAsset(asset)
                                                setForm({
                                                    name: asset.name, category_id: asset.category_id || '',
                                                    type: asset.type, status: asset.status, condition: asset.condition,
                                                    serial_number: asset.serial_number || '', model: asset.model || '',
                                                    manufacturer: asset.manufacturer || '', purchase_date: asset.purchase_date || '',
                                                    purchase_cost: asset.purchase_cost || '', warranty_expiry: asset.warranty_expiry || '',
                                                    location: asset.location || '', assigned_to: asset.assigned_to || '',
                                                    assigned_user: asset.assigned_user || '', ip_address: asset.ip_address || '',
                                                    mac_address: asset.mac_address || '', os: asset.os || '', notes: asset.notes || '',
                                                })
                                                setShowAdd(true)
                                            }}>Edit</Btn>
                                            <Btn size="sm" style={{ color: T.danger }} onClick={() => setConfirmDeleteId(asset.id)}>Delete</Btn>
                                        </div>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
                {filtered.length === 0 && <EmptyState icon="💻" title="No assets found" />}
            </Card>

            {/* Add/Edit Modal */}
            {showAdd && (
                <div style={s.overlay}>
                    <div style={{ ...s.modal, maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' }}>
                        <h2 style={s.modalTitle}>{editingAsset ? 'Edit Asset' : 'New Asset'}</h2>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div style={{ ...s.field, gridColumn: 'span 2' }}>
                                <label style={s.label}>Asset Name *</label>
                                <input style={s.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Dell Laptop XPS 15" />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Category</label>
                                <select style={s.input} value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
                                    <option value="">Select category...</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Type</label>
                                <select style={s.input} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                                    <option value="hardware">Hardware</option>
                                    <option value="software">Software</option>
                                    <option value="network">Network</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Status</label>
                                <select style={s.input} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Condition</label>
                                <select style={s.input} value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })}>
                                    {Object.entries(CONDITION_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Serial Number</label>
                                <input style={s.input} value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Model</label>
                                <input style={s.input} value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Manufacturer</label>
                                <input style={s.input} value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Location</label>
                                <input style={s.input} value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Office, Room 201..." />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Assigned Agent</label>
                                <select style={s.input} value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                                    <option value="">Unassigned</option>
                                    {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Assigned User (Name)</label>
                                <input style={s.input} value={form.assigned_user} onChange={e => setForm({ ...form, assigned_user: e.target.value })} placeholder="Employee name..." />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Purchase Date</label>
                                <input type="date" style={s.input} value={form.purchase_date} onChange={e => setForm({ ...form, purchase_date: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Purchase Cost (SAR)</label>
                                <input type="number" style={s.input} value={form.purchase_cost} onChange={e => setForm({ ...form, purchase_cost: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Warranty Expiry</label>
                                <input type="date" style={s.input} value={form.warranty_expiry} onChange={e => setForm({ ...form, warranty_expiry: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>IP Address</label>
                                <input style={s.input} value={form.ip_address} onChange={e => setForm({ ...form, ip_address: e.target.value })} placeholder="192.168.1.100" />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>MAC Address</label>
                                <input style={s.input} value={form.mac_address} onChange={e => setForm({ ...form, mac_address: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>OS / Software</label>
                                <input style={s.input} value={form.os} onChange={e => setForm({ ...form, os: e.target.value })} placeholder="Windows 11 Pro..." />
                            </div>
                            <div style={{ ...s.field, gridColumn: 'span 2' }}>
                                <label style={s.label}>Notes</label>
                                <textarea style={{ ...s.input, height: 70, padding: '8px 10px', resize: 'none' as const }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                            <Btn variant="ghost" style={{ flex: 1 }} onClick={resetForm}>Cancel</Btn>
                            <Btn variant="primary" style={{ flex: 1 }} onClick={saveAsset}>
                                {editingAsset ? 'Save Changes' : 'Add Asset'}
                            </Btn>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail Modal */}
            {showDetail && (
                <div style={s.overlay} onClick={() => setShowDetail(null)}>
                    <div style={{ ...s.modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{ width: 48, height: 48, borderRadius: 12, background: T.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
                                    {showDetail.itsm_asset_categories?.icon || '💻'}
                                </div>
                                <div>
                                    <p style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{showDetail.name}</p>
                                    <p style={{ fontSize: 12, color: T.text3 }}>{showDetail.asset_tag}</p>
                                </div>
                            </div>
                            <button onClick={() => setShowDetail(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: T.text3 }}>✕</button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            {[
                                { label: 'Status', value: STATUS_CONFIG[showDetail.status]?.label },
                                { label: 'Condition', value: showDetail.condition },
                                { label: 'Category', value: showDetail.itsm_asset_categories?.name },
                                { label: 'Type', value: showDetail.type },
                                { label: 'Serial Number', value: showDetail.serial_number },
                                { label: 'Model', value: showDetail.model },
                                { label: 'Manufacturer', value: showDetail.manufacturer },
                                { label: 'Location', value: showDetail.location },
                                { label: 'Assigned To', value: showDetail.itsm_agents?.name || showDetail.assigned_user },
                                { label: 'IP Address', value: showDetail.ip_address },
                                { label: 'MAC Address', value: showDetail.mac_address },
                                { label: 'OS', value: showDetail.os },
                                { label: 'Purchase Date', value: showDetail.purchase_date ? new Date(showDetail.purchase_date).toLocaleDateString('en-GB') : null },
                                { label: 'Purchase Cost', value: showDetail.purchase_cost ? `SAR ${showDetail.purchase_cost.toLocaleString()}` : null },
                                { label: 'Warranty Expiry', value: showDetail.warranty_expiry ? new Date(showDetail.warranty_expiry).toLocaleDateString('en-GB') : null },
                            ].filter(r => r.value).map(r => (
                                <div key={r.label} style={{ padding: '8px 12px', background: T.surface2, borderRadius: 8 }}>
                                    <p style={{ fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', marginBottom: 3 }}>{r.label}</p>
                                    <p style={{ fontSize: 13, fontWeight: 600, color: T.text, textTransform: 'capitalize' }}>{r.value}</p>
                                </div>
                            ))}
                        </div>

                        {showDetail.notes && (
                            <div style={{ marginTop: 12, padding: '10px 14px', background: T.warningBg, borderRadius: 8, border: `1px solid ${T.warningBorder}` }}>
                                <p style={{ fontSize: 10, fontWeight: 700, color: T.warning, textTransform: 'uppercase', marginBottom: 4 }}>Notes</p>
                                <p style={{ fontSize: 13, color: T.text2 }}>{showDetail.notes}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={confirmDeleteId !== null}
                onClose={() => setConfirmDeleteId(null)}
                onConfirm={() => { if (confirmDeleteId !== null) deleteAsset(confirmDeleteId) }}
                title="Delete asset?"
                message="This will permanently remove the asset record. This action cannot be undone."
                confirmLabel="Delete"
                danger
            />

            <ToastHost />
        </div>
    )
}

const styles: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1300, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px', color: T.text3 },
    back: { background: 'none', border: 'none', color: T.info, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0, fontWeight: 500 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 },
    subtitle: { fontSize: 13, color: T.text2 },
    search: { flex: 1, height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 12px', fontSize: 13, outline: 'none', background: T.surface, color: T.text },
    select: { height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, outline: 'none', background: T.surface, color: T.text2 },
    th: { fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' as const },
    td: { fontSize: 13, padding: '10px 12px', borderBottom: `1px solid ${T.border}`, verticalAlign: 'middle' as const },
    tr: { borderBottom: `1px solid ${T.border}`, cursor: 'pointer' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
    modal: { background: T.surface, borderRadius: 12, padding: 28, width: '100%', maxWidth: 480, border: `1px solid ${T.border}` },
    modalTitle: { fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20 },
    field: { marginBottom: 14 },
    label: { display: 'block', fontSize: 12, fontWeight: 500, color: T.text2, marginBottom: 5 },
    input: { width: '100%', height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, color: T.text, outline: 'none', background: T.surface },
}
